import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import api from '../lib/api';
import { useAuthStore } from '../store/authStore';
import { User, ApiResponse, AcceptInvitationRequest, RegisterRequest, UpdateProfileRequest, UpdatePasswordRequest } from '../types';
import toast from 'react-hot-toast';

interface LoginCredentials {
    email: string;
    password: string;
}

export const useLogin = () => {
    const login = useAuthStore((state) => state.login);

    return useMutation({
        mutationFn: async (credentials: LoginCredentials) => {
            const { data } = await api.post<{
                success: boolean;
                data: { user: User; token: string };
                message?: string;
            }>('/auth/login', credentials);
            console.log('Login API Response:', data);
            return data.data;
        },
        onSuccess: (data) => {
            console.log('Login Success Data:', data);
            login(data.user, data.token);
            toast.success('Login successful!');
        },
        onError: (error: any) => {
            console.error('Login Error:', error);
            console.error('Login Error Response:', error.response?.data);
            const errorMessage = error.response?.data?.error?.message
                || error.response?.data?.message
                || 'Login failed. Please check your credentials.';
            toast.error(errorMessage);
        },
    });
};

export const useRegister = () => {
    const login = useAuthStore((state) => state.login);

    return useMutation({
        mutationFn: async (registerData: RegisterRequest) => {
            const { data } = await api.post<{
                success: boolean;
                data: { user: User; organization: any; token: string };
                message?: string;
            }>('/auth/register', registerData);
            return data.data;
        },
        onSuccess: (data) => {
            login(data.user, data.token);
            toast.success('Registration successful!');
        },
        onError: (error: any) => {
            const errorMessage = error.response?.data?.error?.message
                || error.response?.data?.message
                || 'Registration failed. Please try again.';
            toast.error(errorMessage);
        },
    });
};

export const useLogout = () => {
    const logout = useAuthStore((state) => state.logout);
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: async () => {
            await api.post('/auth/logout');
        },
        onSuccess: () => {
            logout();
            queryClient.clear();
            toast.success('Logged out successfully');
        },
        onError: () => {
            // Even if API call fails, logout locally
            logout();
            queryClient.clear();
        },
    });
};

export const useAcceptInvitation = () => {
    const login = useAuthStore((state) => state.login);

    return useMutation({
        mutationFn: async (acceptData: AcceptInvitationRequest) => {
            const { data } = await api.post<{
                success: boolean;
                data: { user: User; token: string };
                message?: string;
            }>('/team/accept-invitation', acceptData);
            return data.data;
        },
        onSuccess: (data) => {
            login(data.user, data.token);
            toast.success('Welcome to the team! 🎉');
        },
        onError: (error: any) => {
            console.error('Accept Invitation Error:', error.response?.data);
            const errorMessage = error.response?.data?.error?.message
                || error.response?.data?.message
                || 'Failed to accept invitation. Please try again.';
            toast.error(errorMessage);
        },
    });
};

export const useCurrentUser = () => {
    const updateUser = useAuthStore((state) => state.updateUser);

    return useQuery({
        queryKey: ['currentUser'],
        queryFn: async () => {
            const { data } = await api.get<ApiResponse<User>>('/auth/me');
            if (data.data) {
                updateUser(data.data);
            }
            return data.data!;
        },
    });
};

export const useUpdateProfile = () => {
    const queryClient = useQueryClient();
    const updateUser = useAuthStore((state) => state.updateUser);

    return useMutation({
        mutationFn: async (profileData: UpdateProfileRequest) => {
            const { data } = await api.put<ApiResponse<User>>('/auth/profile', profileData);
            return data.data!;
        },
        onSuccess: (data) => {
            updateUser(data);
            queryClient.invalidateQueries({ queryKey: ['currentUser'] });
            toast.success('Profile updated successfully');
        },
        onError: (error: any) => {
            const errorMessage = error.response?.data?.error?.message
                || error.response?.data?.message
                || 'Failed to update profile. Please try again.';
            toast.error(errorMessage);
        },
    });
};

export const useUpdatePassword = () => {
    return useMutation({
        mutationFn: async (passwordData: UpdatePasswordRequest) => {
            const { data } = await api.put<ApiResponse<{ password_changed_at: string }>>('/auth/password', passwordData);
            return data.data;
        },
        onSuccess: () => {
            toast.success('Password updated successfully');
        },
        onError: (error: any) => {
            const errorCode = error.response?.data?.error?.code;
            const errorMessage = error.response?.data?.error?.message
                || error.response?.data?.message;

            if (errorCode === 'SAME_PASSWORD') {
                toast.error('New password must be different from current password');
            } else if (errorMessage?.includes('current password is incorrect')) {
                toast.error('Current password is incorrect');
            } else {
                toast.error(errorMessage || 'Failed to update password. Please try again.');
            }
        },
    });
};

export const usePreferences = () => {
    return useQuery({
        queryKey: ['preferences'],
        queryFn: async () => {
            const { data } = await api.get<ApiResponse<{
                timezone: string;
                locale: 'en' | 'tr';
                theme: 'light' | 'dark' | 'system';
                notifications: any;
            }>>('/auth/preferences');
            return data.data!;
        },
    });
};
